import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import {
  createFeedEvent,
  addFeedComment,
} from '../../store/actions/Projects/ProjectActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import apis from '../../utils/apis';
import { ActivityFeed } from '../shared/feed';

interface ProjectFeedProps {
  projectUuid: string;
}

const ProjectFeed: React.FC<ProjectFeedProps> = ({ projectUuid }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const currentProject = useSelector((state: RootState) => state.projects.currentProject);

  const [feed, setFeed] = useState<any[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);

  const loadFeed = useCallback(async () => {
    if (!projectUuid) return;
    setFeedLoading(true);
    try {
      const response = await apis.get(`/projects/${projectUuid}/feed`, { params: { page: 1, limit: 50 } });
      setFeed(response.data?.feed || []);
    } catch (err) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to load feed'),
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setFeedLoading(false);
    }
  }, [projectUuid, dispatch, t]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  const handlePostFeedEvent = async (eventData: { event_type: string; message: string; metadata?: any }) => {
    try {
      await dispatch(createFeedEvent(projectUuid, eventData.message, eventData.event_type));
      await loadFeed();
    } catch (err) {
      console.error('Error posting feed event:', err);
    }
  };

  const handleAddComment = async (eventId: string, comment: string) => {
    try {
      const numericId = Number(eventId);
      await dispatch(addFeedComment(projectUuid, numericId, comment));
      await loadFeed();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('Unknown error');
      dispatch(showFeedbackModal({
        showModal: true,
        message: `Comment failed: ${errorMessage}`,
        modalType: 'error',
        duration: 5000,
      }));
    }
  };

  const projectMembers = useMemo(() => {
    const collabs = (currentProject as any)?.shared_with || [];
    return collabs.map((c: any) => {
      const fullName: string = c.name || c.email || '';
      const [first, ...rest] = fullName.split(' ');
      return {
        id: String(c.user_id || c.id || ''),
        first_name: first || c.email || '',
        last_name: rest.join(' ') || '',
        email: c.email || '',
      };
    });
  }, [currentProject]);

  return (
    <div className="flex-1 min-h-0 flex flex-col px-4 pb-3">
      <ActivityFeed
        events={feed}
        loading={feedLoading}
        onRefresh={loadFeed}
        onPostEvent={handlePostFeedEvent}
        onCommentAdd={handleAddComment}
        currentUser={
          userInfo
            ? {
                id: userInfo.UserID || 'current-user',
                first_name: userInfo.FirstName || 'You',
                last_name: userInfo.LastName || '',
                email: userInfo.Email || '',
              }
            : undefined
        }
        members={projectMembers}
      />
    </div>
  );
};

export default ProjectFeed;
